"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.drawCanvasLineChart = void 0;
const lodash_1 = __importDefault(require("lodash"));
const canvas_1 = require("canvas");
const constants_1 = require("./constants");
const helpers_1 = require("./helpers");
const CanvasDrawer_1 = __importDefault(require("../../util/CanvasDrawer"));
const chart_helpers_1 = require("../../util/chart.helpers");
const common_module_scss_1 = __importDefault(require("../../styles/common.module.scss"));
function drawCanvasLineChart(data, parent, params, showText, canvas, xOffset, yOffset, highlightValue, sparkline) {
  const context = canvas.getContext('2d');
  const defaultLabelWidth = Math.min(150, this.width * 0.4);
  const maxLabelWidth = params.margins.maxRightLabel === undefined ? defaultLabelWidth : params.margins.maxRightLabel;
  const showLabels = showText && (!this.isSubchart || data.length > 1);
  const labelMode = (0, chart_helpers_1.getLabelTextMode)(data, params);
  const sizingLabels = data.map(d => this.getLabel(d, this.isSubchart, labelMode, params));
  const labelSize = (0, helpers_1.getLabelSizing)(sizingLabels, maxLabelWidth, params.fontSize);
  const {
    xScale,
    yScale
  } = this.createScales({
    data,
    params
  }, showLabels ? labelSize.width : 0);
  const axisFormat = t => (0, chart_helpers_1.valueFormat)({
    data,
    parent,
    params
  })(t, {
    ticks: yScale.ticks(this.yTicks)
  });
  if (showText) {
    if (showLabels) {
      const labels = data.map((label, i) => {
        const labelText = this.getLabel(label, this.isSubchart, labelMode, params);
        return {
          text: (0, chart_helpers_1.breakLines)(labelText, labelSize.width, `${labelSize.fontWeight} ${labelSize.fontSize}px ${common_module_scss_1.default.mainFontFamily}`),
          y: yScale(label.metric.data.slice(-1)[0].value),
          primaryColor: this.colourFunction(label, i, params)
        };
      });
      CanvasDrawer_1.default.drawLineLabels(this, canvas, (0, helpers_1.forceLabels)(labels, {
        maxY: yScale.range()[0],
        fontSize: params.fontSize,
        labelLineHeight: constants_1.LABEL_LINE_HEIGHT
      }), params, xScale, constants_1.LABEL_PADDING, params.fontSize, xOffset, yOffset);
    }
    CanvasDrawer_1.default.drawLineChartAxis(canvas, data, params, xScale, yScale, axisFormat, this.yTicks, xOffset, yOffset);
  }
  if (sparkline) {
    const defaultLineSize = 4;
    const lineSize = data[0].lineSize || params.lineSize || defaultLineSize;
    const radius = this.circleR * lineSize / defaultLineSize;
    CanvasDrawer_1.default.drawSparklineDecorators(canvas, data, params, xScale, yScale, this.colourFunction(data[0], 0, params), axisFormat, radius, xOffset, yOffset);
  }
  CanvasDrawer_1.default.drawLines(this, canvas, data, params, xScale, yScale, params.areaFill && data.length === 1, xOffset, yOffset);
  if (highlightValue !== undefined) {
    const defaultLineSize = 4;
    const lineSize = data[0].lineSize || params.lineSize || defaultLineSize;
    const circleR = this.circleR * lineSize / defaultLineSize;
    const x = xScale(highlightValue.key) + xOffset;
    const y = yScale(highlightValue.value) + yOffset;
    context.fillStyle = this.colourFunction(data[0], 0, params);
    context.beginPath();
    context.ellipse(x, y, circleR, circleR, 0, 0, 2 * Math.PI);
    context.fill();
  }
}
exports.drawCanvasLineChart = drawCanvasLineChart;
async function drawCanvas(dataPromises) {
  var _data$, _data$$metric, _firstData;
  let chartOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let {
    margin = 40,
    width = 720,
    scale = 3,
    key: optionKey = undefined,
    showText = true,
    showBranding = true,
    xOffset = 0,
    yOffset = 0,
    sparkline = false
  } = arguments.length > 2 ? arguments[2] : undefined;
  let canvas = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : (0, canvas_1.createCanvas)(width, width);
  const allData = await this.data(dataPromises, chartOptions);
  if (!allData) return null;
  const {
    data,
    parent
  } = allData;
  // using metric.data instead of metric.datasets[0].data gives the
  // pre-transformed date keys instead of date strings
  const firstData = ((_data$ = data[0]) === null || _data$ === void 0 ? void 0 : (_data$$metric = _data$.metric) === null || _data$$metric === void 0 ? void 0 : _data$$metric.data) || [];
  const key = optionKey !== undefined ? optionKey : (_firstData = firstData[firstData.length - 1]) === null || _firstData === void 0 ? void 0 : _firstData.key;
  const params = (0, helpers_1.getDrawParams)({
    data,
    parent,
    options: chartOptions
  });
  if (margin !== null) {
    params.margins.left = margin;
    params.margins.right = margin;
  }
  if (showText === false && sparkline === false) {
    params.margins.fixedAxis = 0;
  }
  const showValue = data.length === 1 && !this.isSubchart;
  const logoOffset = margin + (showText && showBranding ? 40 : 0);
  const titleOffset = showText ? params.titleFontSize * parseFloat(common_module_scss_1.default.chartTitleLineHeight) : 0;
  const hasSubtitle = showText && params.subtitle !== '';
  const subtitleOffset = hasSubtitle ? params.subtitleFontSize * parseFloat(common_module_scss_1.default.chartSubtitleLineHeight) : 0;
  const valueOffset = showText && showValue ? params.titleFontSize * parseFloat(common_module_scss_1.default.chartTitleLineHeight) : 0;
  const footerHeight = margin + (showText && showBranding ? 20 : 0);
  const chartHeight = width * (params.heightToWidthRatio || 0.5);
  // Canvas modification is meant to have side effects
  // eslint-disable-next-line no-param-reassign
  canvas.width = width * scale;
  // eslint-disable-next-line no-param-reassign
  canvas.height = (chartHeight + logoOffset + titleOffset + subtitleOffset + valueOffset + footerHeight) * scale;
  const context = canvas.getContext('2d');
  context.fillStyle = 'white';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.scale(scale, scale);
  let highlightValue;
  if (showValue && key) {
    highlightValue = (0, lodash_1.default)(data[0].metric.data).find(d => d.key.getTime() === key.getTime());
    if (highlightValue === undefined) {
      highlightValue = (0, lodash_1.default)(data[0].metric.data).maxBy('key');
    }
    const defaultLineSize = 4;
    const lineSize = data[0].lineSize || params.lineSize || defaultLineSize;
    const circleR = this.circleR * lineSize / defaultLineSize;
    params.margins.right = Math.max(circleR, params.margins.right);
  }
  const chartOffset = yOffset + logoOffset + titleOffset + subtitleOffset + valueOffset;
  if (showText) {
    CanvasDrawer_1.default.drawTitles.call(this, canvas, params, undefined, undefined, undefined, undefined, xOffset, yOffset + logoOffset);
    if (highlightValue) {
      context.textBaseline = 'alphabetic';
      context.font = `600 ${params.titleFontSize}px ${common_module_scss_1.default.mainFontFamily}`;
      context.fillStyle = this.colourFunction(data[0], 0, params);
      const y = yOffset + logoOffset + titleOffset + subtitleOffset + (valueOffset + params.titleFontSize) / 2;
      context.fillText((0, chart_helpers_1.tooltipFormat)({
        data,
        parent,
        params
      })(highlightValue.value), params.margins.left, y);
      const valueWidth = context.measureText(`${(0, chart_helpers_1.tooltipFormat)({
        data,
        parent,
        params
      })(highlightValue.value)} `).width;
      context.font = `${params.subtitleFontSize}px ${common_module_scss_1.default.mainFontFamily}`;
      context.fillStyle = common_module_scss_1.default.chartSubitleColor;
      const type = data[0].metric.xType;
      const inBetweenWord = type === 'days' || type === 'daily' ? 'on' : 'in';
      context.fillText(`${inBetweenWord} ${(0, chart_helpers_1.dateFormat)(highlightValue.key, data[0].metric.xType)}`, params.margins.left + valueWidth, y);
    }
    if (showBranding) {
      await CanvasDrawer_1.default.drawLogo(canvas, width, params, margin);
      CanvasDrawer_1.default.drawFooter(canvas, width, chartOffset + width * (params.heightToWidthRatio || 0.5) + 20);
    }
  }
  const origWidth = this.width;
  const origHeight = this.height;
  this.width = width;
  this.height = chartHeight;
  drawCanvasLineChart.call(this, data, parent, params, showText, canvas, xOffset, chartOffset, highlightValue, sparkline);
  this.width = origWidth;
  this.height = origHeight;
  return canvas;
}
exports.default = drawCanvas;